// Sidebar configuration — single source of truth used by AppShell.
// Extracted from AppShell.js as part of the tech-debt refactor.
// platformOnly: platform admin only; allowRoles: those roles plus the platform admin (see lib/sidebarVisibility).

import {
  LayoutDashboard, Users, UserCircle, DollarSign, Briefcase,
  Calendar, ClipboardCheck, FileText, Clock, Heart, MessageSquare,
  Sparkles, Lightbulb, Settings, Home, AlertTriangle, Package, Car,
} from 'lucide-react';
import { canBrowseLibrary, canAdminLibrary } from '@/lib/libraryAccess';
import { canViewRegisters } from '@/lib/auditRegisters';
import { canViewReadiness, canExportReadiness } from '@/lib/readiness';

// ════════════════════════════════════════════════════════════════
// SIDEBAR V1 — Original layout (default)
// ════════════════════════════════════════════════════════════════
export const sidebarConfig = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    items: [
      { name: 'Overview', path: '/dashboard', permissions: ['dashboard.view'] },
      { name: 'Operations', path: '/dashboard/operations', permissions: ['dashboard.view'] },
      { name: 'Action Items', path: '/dashboard/actions', permissions: ['dashboard.view'], badge: 'actions' },
      { name: 'Alerts', path: '/alerts', permissions: ['alerts.view'], badge: 'alerts' },
    ],
  },
  {
    title: 'Participants',
    icon: Users,
    items: [
      { name: 'Participants', path: '/participants/list', permissions: ['participants.view', 'participants.manage'] },
      { name: 'Teams', path: '/teams', permissions: ['teams.view'] },
      { name: 'Appointments', path: '/engagement/appointments', permissions: ['appointments.view'] },
      { name: 'Referrals & Intake', path: '/intake/referrals', permissions: ['intake.view', 'participants.view', 'participants.manage'] },
      { name: 'Accommodation', path: '/accommodation/dashboard', permissions: ['houses.view'] },
      { name: 'Properties', path: '/accommodation/houses', permissions: ['houses.view'] },
      { name: 'Service Catalog', path: '/admin/services', permissions: ['services.manage'] },
      { name: 'Branch Catalogue', path: '/admin/branch-catalogue', permissions: ['services.manage'] },
    ],
  },
  {
    title: 'Workforce',
    icon: Briefcase,
    items: [
      { name: 'Workforce Intelligence', path: '/workforce/intelligence', permissions: ['staff.view'] },
      { name: 'Document Intelligence', path: '/compliance/document-intelligence', permissions: ['compliance.view'] },
      { name: 'My Shifts', path: '/roster/my-shifts', permissions: ['roster.view'] },
      { name: 'Staff Profiles', path: '/staff/list', permissions: ['staff.view', 'staff.manage'] },
      { name: 'Roster', path: '/roster/calendar', platformOnly: true, permissions: ['roster.view', 'roster.manage'] },
      { name: 'Visual Calendar', path: '/roster/visual', platformOnly: true, permissions: ['roster.view', 'roster.manage'] },
      { name: 'Scheduling Board', path: '/roster/scheduling-board', permissions: ['roster.view', 'roster.manage'] },
      { name: 'Workforce Dashboard', path: '/roster/workforce', permissions: ['roster.view'] },
      { name: 'SIL Rostering', path: '/roster/sil', permissions: ['roster.view', 'roster.manage'] },
      { name: 'Centre Sessions', path: '/admin/centre-sessions', permissions: ['roster.view', 'roster.create'] },
      { name: 'Team Rostering', path: '/roster/teams', permissions: ['roster.view', 'roster.manage'] },
      { name: 'Staff Availability', path: '/roster/staff-availability', permissions: ['availability.view', 'roster.manage'] },
      { name: 'Availability Overview', path: '/workforce/availability', permissions: ['availability.view'] },
      { name: 'Recurring Shifts', path: '/roster/recurring', permissions: ['roster.manage_templates'] },   // RP-3 R4: LEGACY series engine — Admin / Roster Manager only (Phase 12 retire)
      { name: 'Shift Templates', path: '/roster/templates', permissions: ['roster.manage_templates', 'roster.manage'] },
      { name: 'Task Templates', path: '/admin/task-templates', permissions: ['roster.view', 'roster.manage'] },
      { name: 'Note Templates', path: '/admin/note-templates', permissions: ['notes.create', 'roster.manage'] },
      { name: 'Incident Review', path: '/admin/incident-review', permissions: ['incidents.review', 'incidents.manage', 'roster.manage'] },
      { name: 'Swap Requests', path: '/roster/swap-requests', permissions: ['roster.approve_swap', 'roster.manage'] },
      { name: 'Bulk Assign', path: '/roster/bulk-assign', permissions: ['roster.manage'] },
      { name: 'Timesheet Approval', path: '/timesheets/approval', permissions: ['timesheets.approve'] },
      { name: 'Exception Queue', path: '/admin/attendance-exceptions', permissions: ['timesheets.approve'] },
      { name: 'Corrections', path: '/admin/corrections', permissions: ['shift.edit_post_completion', 'timesheet.edit_after_approval'] },
      { name: 'Timesheets (Legacy)', path: '/timesheets/pending', permissions: ['timesheets.approve'] },
      { name: 'Leave Management', path: '/hr/leave', permissions: ['leave.view', 'leave.approve'], badge: 'leave' },
      { name: 'Staff Compliance', path: '/compliance/staff', permissions: ['compliance.view'] },
      { name: 'Worker Screening', path: '/compliance/worker-screening', permissions: ['staff.hr.view', 'staff.hr.manage'] },
      { name: 'Worker Feedback', path: '/compliance/worker-feedback', permissions: ['matching.feedback.triage', 'matching.restrictions.manage'] },
      { name: 'Recruitment', path: '/hr/recruitment', permissions: ['recruitment.view', 'candidates.view'] },
      { name: 'Document Review', path: '/hr/document-review', permissions: ['recruitment.view'] },
      { name: 'HR Dashboard', path: '/hr/dashboard', permissions: ['recruitment.view'] },
    ],
  },
  {
    title: 'Clinical & Safety',
    icon: Heart,
    items: [
      { name: 'Medications', path: '/clinical/medications', permissions: ['medications.view', 'emar.view'] },
      { name: 'Incidents', path: '/compliance/incidents', permissions: ['incidents.view', 'incidents.create'], badge: 'incidents' },
      { name: 'Incident Intelligence', path: '/compliance/incidents/intelligence', permissions: ['incidents.dashboard'] },
      { name: 'Restrictive Practices', path: '/compliance/restrictive-practices', permissions: ['restrictive_practices.view'] },
      { name: 'Risk Dashboard', path: '/insights/risk', permissions: ['reports.manage'] },
      { name: 'Audit Logs', path: '/admin/audit', permissions: ['audit.read'] },
    ],
  },
  {
    title: 'Finance',
    icon: DollarSign,
    items: [
      { name: 'Finance Pipeline', path: '/finance/pipeline', permissions: ['claims.view'] },
      { name: 'Billing Readiness', path: '/finance/billing-readiness', permissions: ['timesheets.view'] },
      { name: 'Service Evidence', path: '/timesheets/evidence', permissions: ['timesheets.review', 'timesheets.approve'] },
      { name: 'Billing Records', path: '/finance/billing-records', permissions: ['invoices.manage', 'claims.view'] },
      { name: 'Invoices', path: '/finance/invoices', permissions: ['invoices.manage'] },
      { name: 'Invoice Regeneration', path: '/finance/invoice-regeneration', permissions: ['invoice.regenerate', 'invoice.adjust'] },
      { name: 'Claims', path: '/finance/claims', permissions: ['claims.view', 'claims.generate'], badge: 'claims' },
      { name: 'Missed Claims', path: '/finance/missed-claims', permissions: ['claims.view'], badge: 'revenue' },
      { name: 'Payroll Periods', path: '/finance/payroll-periods', permissions: ['payroll.view', 'payroll.manage'] },
      { name: 'Payroll', path: '/finance/payroll', permissions: ['payroll.batch.view', 'payroll.batch.manage'] },
      { name: 'Payroll Engine', path: '/admin/payroll', permissions: ['staff.payroll.view', 'payroll.export'] },
      { name: 'Cost Reports', path: '/reports/staff-costs', permissions: ['payroll.view'] },
      { name: 'Funding Forecast', path: '/insights/funding', permissions: ['reports.manage'] },
      { name: 'Accounting', path: '/settings/integrations/accounting', permissions: ['integrations.accounting.view'] },
    ],
  },
  {
    title: 'Communication',
    icon: MessageSquare,
    items: [
      { name: 'Communication Hub', path: '/communications/hub', permissions: ['communications.view'] },
      { name: 'Messages', path: '/engagement/messages', permissions: ['engagement.messages.view'] },
      { name: 'SMS Log', path: '/communications/sms', permissions: ['communications.sms.view'] },
      { name: 'Email Log', path: '/communications/email', permissions: ['communications.email.view'] },
      { name: 'Feedback', path: '/engagement/feedback', permissions: ['engagement.feedback.view'] },
      { name: 'Announcements', path: '/engagement/announcements', permissions: ['engagement.announcements.view'] },
      { name: 'Templates', path: '/settings/communications/templates', permissions: ['communications.templates.view'] },
    ],
  },
  {
    title: 'Insights & AI',
    icon: Lightbulb,
    items: [
      { name: 'AI Intelligence', path: '/ai/intelligence', permissions: ['reports.manage'] },
      { name: 'Reports', path: '/reports', permissions: ['reports.manage'] },
      { name: 'Recommendations', path: '/insights/recommendations', permissions: ['reports.manage'] },
      { name: 'Saved Insights', path: '/insights/saved', permissions: ['insights.manage'] },
      { name: 'AI Copilot', path: '/ai', permissions: ['ai.query'] },
      { name: 'Roster Intelligence', path: '/roster/insights', permissions: ['roster.view'] },
    ],
  },
  {
    title: 'Forms',
    icon: ClipboardCheck,
    items: [
      { name: 'Dashboard', path: '/forms/dashboard', permissions: ['forms.view'] },
      { name: 'Templates', path: '/forms/templates', permissions: ['forms.view'] },
      { name: 'Template Library', path: '/forms/library', permissions: ['forms.view'], visibleWhen: canBrowseLibrary },
      { name: 'Audit Registers', path: '/audit/registers', permissions: ['audit_registers.view'], visibleWhen: canViewRegisters },
      { name: 'NDIS Audit Readiness', path: '/audit/readiness', permissions: ['readiness.view'], visibleWhen: canViewReadiness },
      { name: 'Audit Evidence Bundle', path: '/audit/readiness/bundle', permissions: ['readiness.export'], visibleWhen: canExportReadiness },
      { name: 'Assignments', path: '/forms/assignments', permissions: ['forms.view'] },
    ],
  },
  {
    title: 'Asset Management',
    icon: Package,
    items: [
      { name: 'Asset Register', path: '/asset-management', permissions: ['assets.view'] },
      { name: 'Maintenance Requests', path: '/maintenance', permissions: ['maintenance.view', 'maintenance.manage'] },
    ],
  },
  {
    title: 'Transport',
    icon: Car,
    items: [
      { name: 'Trip Logs', path: '/admin/transport/logs', permissions: ['transport.view_all', 'transport.approve'], featureFlag: 'transport_logging_enabled' },
      { name: 'Vehicles', path: '/admin/transport/vehicles', permissions: ['transport.manage_vehicles', 'vehicles.view'], featureFlag: 'transport_logging_enabled' },
      { name: 'Settings', path: '/admin/transport/settings', permissions: ['transport.configure'], featureFlag: 'transport_logging_enabled' },
    ],
  },
  {
    title: 'Administration',
    icon: Settings,
    items: [
      { name: 'Users', path: '/admin/users', permissions: ['auth.manage_users'] },
      { name: 'NDIS Master Catalogue', path: '/admin/ndis-catalogue', permissions: ['roster.view'], requireRole: 'Super Admin' },
      { name: 'Roles', path: '/admin/roles', permissions: ['auth.manage_roles'] },
      { name: 'Care-Plan Templates', path: '/admin/care-plan-templates', permissions: ['careplan_templates.view'], featureFlag: 'participant_careplan_template_management_enabled' },
      { name: 'Security', path: '/admin/security', permissions: ['security.audit.view'] },
      { name: 'Security Alerts', path: '/admin/security/alerts', permissions: ['security.audit.view'] },
      { name: 'Public Forms', path: '/settings/public-forms', permissions: ['org.view'] },
      { name: 'Webhooks', path: '/settings/integrations/webhooks', permissions: ['integrations.webhooks.view'] },
      { name: 'Settings', path: '/org/settings', permissions: ['org.view'] },
      { name: 'Branding', path: '/admin/settings/branding', platformOnly: true, permissions: ['org.manage_settings'] },   // backend also requires a platform admin (require_platform_global)
      { name: 'Feature Flags', path: '/admin/settings/feature-flags', platformOnly: true, permissions: ['org.manage_settings'] },
      { name: 'Ratio Rules', path: '/admin/settings/ratio-rules', allowRoles: ['Admin'], permissions: ['org.manage_settings'] },
      { name: 'Sleep-Over Settings', path: '/admin/settings/sleepover', allowRoles: ['Admin'], permissions: ['org.manage_settings'], featureFlag: 'sleepover_billing_enabled' },
      { name: 'Sleep-Over Comparison', path: '/admin/sleepover/estimate-comparison', allowRoles: ['Admin'], permissions: ['roster.view'], featureFlag: 'sleepover_billing_enabled' },
      { name: 'Group Sessions', path: '/admin/group-sessions', permissions: ['roster.view', 'roster.manage'] },
      { name: 'Reports', path: '/admin/reports', permissions: ['roster.view'] },
      { name: 'Rollout Control', path: '/admin/rollout', platformOnly: true, permissions: ['org.manage_settings'] },
      { name: 'Release Sign-off', path: '/admin/release-signoff', platformOnly: true, permissions: ['org.manage_settings'] },
      { name: 'Daily Digest', path: '/admin/digest', permissions: ['org.manage_settings'] },
      { name: 'System Admin', path: '/admin/system', permissions: ['security.audit.view'] },
      { name: 'Audit Reporting', path: '/admin/audit-reporting', permissions: ['reports.manage', 'audit.read'] },
      { name: 'Soft Launch', path: '/admin/soft-launch', platformOnly: true, permissions: ['org.manage_settings'] },
      { name: 'Client Onboarding', path: '/admin/onboarding', platformOnly: true, permissions: ['org.manage_settings'], requireRole: 'Super Admin', featureFlag: 'client_onboarding_enabled' },
    ],
  },
];

// ════════════════════════════════════════════════════════════════
// SIDEBAR V2 — Workflow-driven restructure (gated by sidebar_v2_structure_enabled)
// ════════════════════════════════════════════════════════════════
export const sidebarConfigV2 = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    items: [
      { name: 'Overview', path: '/dashboard', permissions: ['dashboard.view'] },
      { name: 'Operations', path: '/dashboard/operations', permissions: ['dashboard.view'] },
      { name: 'Action Items', path: '/dashboard/actions', permissions: ['dashboard.view'], badge: 'actions' },
      { name: 'Alerts', path: '/alerts', permissions: ['alerts.view'], badge: 'alerts' },
    ],
  },
  {
    title: 'Participants',
    icon: Users,
    items: [
      { name: 'Participants', path: '/participants/list', permissions: ['participants.view', 'participants.manage'] },
      { name: 'Teams', path: '/teams', permissions: ['teams.view'] },
      { name: 'Appointments', path: '/engagement/appointments', permissions: ['appointments.view'] },
      { name: 'Referrals & Intake', path: '/intake/referrals', permissions: ['intake.view', 'participants.view', 'participants.manage'] },
      { name: 'Accommodation', path: '/accommodation/dashboard', permissions: ['houses.view'] },
      { name: 'Properties', path: '/accommodation/houses', permissions: ['houses.view'] },
      { name: 'SIL Intelligence', path: '/accommodation/sil-intelligence', permissions: ['houses.view', 'reports.manage'] },
      { name: 'Service Catalog', path: '/admin/services', permissions: ['services.manage'] },
      { name: 'Branch Catalogue', path: '/admin/branch-catalogue', permissions: ['services.manage'] },
    ],
  },
  {
    title: 'Scheduling & Rostering',
    icon: Calendar,
    items: [
      { name: 'My Shifts', path: '/roster/my-shifts', permissions: ['roster.view'] },
      { name: 'Roster', path: '/roster/calendar', platformOnly: true, permissions: ['roster.view', 'roster.manage'] },
      { name: 'Visual Calendar', path: '/roster/visual', platformOnly: true, permissions: ['roster.view', 'roster.manage'] },
      { name: 'Scheduling Board', path: '/roster/scheduling-board', permissions: ['roster.view', 'roster.manage'] },
      { name: 'SIL Rostering', path: '/roster/sil', permissions: ['roster.view', 'roster.manage'] },
      { name: 'Centre Sessions', path: '/admin/centre-sessions', permissions: ['roster.view', 'roster.create'] },
      { name: 'Team Rostering', path: '/roster/teams', permissions: ['roster.view', 'roster.manage'] },
      { name: 'Staff Availability', path: '/roster/staff-availability', permissions: ['availability.view', 'roster.manage'] },
      { name: 'Availability Overview', path: '/workforce/availability', permissions: ['availability.view'] },
      { name: 'Recurring Shifts', path: '/roster/recurring', permissions: ['roster.manage_templates'] },   // RP-3 R4: LEGACY series engine — Admin / Roster Manager only (Phase 12 retire)
      { name: 'Shift Templates', path: '/roster/templates', permissions: ['roster.manage_templates', 'roster.manage'] },
      { name: 'Task Templates', path: '/admin/task-templates', permissions: ['roster.view', 'roster.manage'] },
      { name: 'Note Templates', path: '/admin/note-templates', permissions: ['notes.create', 'roster.manage'] },
      { name: 'Incident Review', path: '/admin/incident-review', permissions: ['incidents.review', 'incidents.manage', 'roster.manage'] },
      { name: 'Swap Requests', path: '/roster/swap-requests', permissions: ['roster.approve_swap', 'roster.manage'] },
      { name: 'Bulk Assign', path: '/roster/bulk-assign', permissions: ['roster.manage'] },
    ],
  },
  {
    title: 'Workforce',
    icon: Briefcase,
    items: [
      { name: 'Workforce Intelligence', path: '/workforce/intelligence', permissions: ['staff.view'] },
      { name: 'Staff Profiles', path: '/staff/list', permissions: ['staff.view', 'staff.manage'] },
      { name: 'Workforce Dashboard', path: '/roster/workforce', permissions: ['roster.view'] },
      { name: 'Leave Management', path: '/hr/leave', permissions: ['leave.view', 'leave.approve'], badge: 'leave' },
      { name: 'Staff Compliance', path: '/compliance/staff', permissions: ['compliance.view'] },
      { name: 'Worker Screening', path: '/compliance/worker-screening', permissions: ['staff.hr.view', 'staff.hr.manage'] },
      { name: 'Worker Feedback', path: '/compliance/worker-feedback', permissions: ['matching.feedback.triage', 'matching.restrictions.manage'] },
      { name: 'Staff Policies', path: '/staff/policies', permissions: ['staff.view'] },
      { name: 'Recruitment', path: '/hr/recruitment', permissions: ['recruitment.view', 'candidates.view'] },
      { name: 'HR Dashboard', path: '/hr/dashboard', permissions: ['recruitment.view'] },
      { name: 'HR KPIs', path: '/dashboard/hr', permissions: ['dashboard.view', 'recruitment.view'] },
    ],
  },
  {
    title: 'Timesheets & Payroll',
    icon: Clock,
    items: [
      { name: 'Timesheet Approval', path: '/timesheets/approval', permissions: ['timesheets.approve'] },
      { name: 'Exception Queue', path: '/admin/attendance-exceptions', permissions: ['timesheets.approve'] },
      { name: 'Corrections', path: '/admin/corrections', permissions: ['shift.edit_post_completion', 'timesheet.edit_after_approval'] },
      { name: 'Timesheets (Legacy)', path: '/timesheets/pending', permissions: ['timesheets.approve'] },
      { name: 'Payroll Periods', path: '/finance/payroll-periods', permissions: ['payroll.view', 'payroll.manage'] },
      { name: 'Payroll', path: '/finance/payroll', permissions: ['payroll.batch.view', 'payroll.batch.manage'] },
      { name: 'Payroll Engine', path: '/admin/payroll', permissions: ['staff.payroll.view', 'payroll.export'] },
    ],
  },
  {
    title: 'Clinical & Safety',
    icon: Heart,
    items: [
      { name: 'Medications', path: '/clinical/medications', permissions: ['medications.view', 'emar.view'] },
      { name: 'Incidents', path: '/compliance/incidents', permissions: ['incidents.view', 'incidents.create'], badge: 'incidents' },
      { name: 'Incident Intelligence', path: '/compliance/incidents/intelligence', permissions: ['incidents.dashboard'] },
      { name: 'Restrictive Practices', path: '/compliance/restrictive-practices', permissions: ['restrictive_practices.view'] },
      { name: 'Risk Dashboard', path: '/insights/risk', permissions: ['reports.manage'] },
      { name: 'Chart Review Outcomes', path: '/charts/review-outcomes', permissions: ['charts.view', 'compliance.view'] },
      { name: 'Clinical KPIs', path: '/dashboard/clinical', permissions: ['dashboard.view'] },
      { name: 'Audit Logs', path: '/admin/audit', permissions: ['audit.read'] },
    ],
  },
  {
    title: 'Finance',
    icon: DollarSign,
    items: [
      { name: 'Finance Pipeline', path: '/finance/pipeline', permissions: ['claims.view'] },
      { name: 'Billing Readiness', path: '/finance/billing-readiness', permissions: ['timesheets.view'] },
      { name: 'Service Evidence', path: '/timesheets/evidence', permissions: ['timesheets.review', 'timesheets.approve'] },
      { name: 'Billing Records', path: '/finance/billing-records', permissions: ['invoices.manage', 'claims.view'] },
      { name: 'Invoices', path: '/finance/invoices', permissions: ['invoices.manage'] },
      { name: 'Invoice Regeneration', path: '/finance/invoice-regeneration', permissions: ['invoice.regenerate', 'invoice.adjust'] },
      { name: 'Claims', path: '/finance/claims', permissions: ['claims.view', 'claims.generate'], badge: 'claims' },
      { name: 'Missed Claims', path: '/finance/missed-claims', permissions: ['claims.view'], badge: 'revenue' },
      { name: 'Cost Reports', path: '/reports/staff-costs', permissions: ['payroll.view'] },
      { name: 'Funding Forecast', path: '/insights/funding', permissions: ['reports.manage'] },
      { name: 'Finance KPIs', path: '/dashboard/finance', permissions: ['dashboard.view', 'reports.manage'] },
      { name: 'Accounting', path: '/settings/integrations/accounting', permissions: ['integrations.accounting.view'] },
    ],
  },
  {
    title: 'Communication',
    icon: MessageSquare,
    items: [
      { name: 'Communication Hub', path: '/communications/hub', permissions: ['communications.view'] },
      { name: 'Messages', path: '/engagement/messages', permissions: ['engagement.messages.view'] },
      { name: 'Notifications Inbox', path: '/notifications', permissions: ['dashboard.view'] },
      { name: 'SMS Log', path: '/communications/sms', permissions: ['communications.sms.view'] },
      { name: 'Email Log', path: '/communications/email', permissions: ['communications.email.view'] },
      { name: 'Feedback', path: '/engagement/feedback', permissions: ['engagement.feedback.view'] },
      { name: 'Announcements', path: '/engagement/announcements', permissions: ['engagement.announcements.view'] },
      { name: 'Message Templates', path: '/settings/communications/templates', permissions: ['communications.templates.view'] },
    ],
  },
  {
    title: 'Documents & Forms',
    icon: FileText,
    items: [
      { name: 'Document Library', path: '/documents', permissions: ['documents.view', 'compliance.view'] },
      { name: 'Document Templates', path: '/documents/templates', permissions: ['documents.view', 'compliance.view'] },
      { name: 'E-Sign Requests', path: '/documents/esign', permissions: ['documents.view', 'compliance.view'] },
      { name: 'Document Export', path: '/documents/export', permissions: ['documents.view', 'compliance.view'] },
      { name: 'Document Intelligence', path: '/compliance/document-intelligence', permissions: ['compliance.view'] },
      { name: 'Document Review', path: '/hr/document-review', permissions: ['recruitment.view'] },
      { name: 'Forms Dashboard', path: '/forms/dashboard', permissions: ['forms.view'] },
      { name: 'Form Templates', path: '/forms/templates', permissions: ['forms.view'] },
      { name: 'Template Library', path: '/forms/library', permissions: ['forms.view'], visibleWhen: canBrowseLibrary },
      { name: 'Audit Registers', path: '/audit/registers', permissions: ['audit_registers.view'], visibleWhen: canViewRegisters },
      { name: 'NDIS Audit Readiness', path: '/audit/readiness', permissions: ['readiness.view'], visibleWhen: canViewReadiness },
      { name: 'Audit Evidence Bundle', path: '/audit/readiness/bundle', permissions: ['readiness.export'], visibleWhen: canExportReadiness },
      { name: 'Assignments', path: '/forms/assignments', permissions: ['forms.view'] },
    ],
  },
  // Day-30+ tooling — orchestration consoles + admin power-user surfaces
  {
    title: 'Admin Tools',
    icon: Sparkles,
    items: [
      { name: 'Activation Command Center', path: '/admin/allocations/command-center', platformOnly: true, permissions: ['org.manage_settings'] },
      { name: 'Live Split Billing Governance', path: '/admin/allocations/live-governance', platformOnly: true, permissions: ['org.manage_settings'] },
      { name: 'Parity Audit', path: '/admin/allocations/parity-audit', platformOnly: true, permissions: ['org.manage_settings'] },
      { name: 'Compliance Dashboard', path: '/admin/compliance', platformOnly: true, permissions: ['compliance.view', 'org.manage_settings'] },
      { name: 'Predictive Staffing', path: '/admin/predictive-staffing', platformOnly: true, permissions: ['reports.manage'] },
      { name: 'Revenue Optimization', path: '/admin/revenue', platformOnly: true, permissions: ['reports.manage'] },
      { name: 'Smart Alerts', path: '/admin/smart-alerts', platformOnly: true, permissions: ['alerts.view'] },
      { name: 'New Onboarding (wizard)', path: '/admin/onboarding/new', platformOnly: true, permissions: ['org.manage_settings'], requireRole: 'Super Admin' },
      { name: 'Library Packs', path: '/forms/library/packs', platformOnly: true, permissions: ['forms.library_admin'], visibleWhen: canAdminLibrary },
    ],
  },
  {
    title: 'Insights & AI',
    icon: Sparkles,
    items: [
      { name: 'AI Intelligence', path: '/ai/intelligence', permissions: ['reports.manage'] },
      { name: 'Reports', path: '/reports', permissions: ['reports.manage'] },
      { name: 'Recommendations', path: '/insights/recommendations', permissions: ['reports.manage'] },
      { name: 'Saved Insights', path: '/insights/saved', permissions: ['insights.manage'] },
      { name: 'AI Copilot', path: '/ai', permissions: ['ai.query'] },
      { name: 'Roster Intelligence', path: '/roster/insights', permissions: ['roster.view'] },
    ],
  },
  {
    title: 'Asset Management',
    icon: Package,
    items: [
      { name: 'Asset Register', path: '/asset-management', permissions: ['assets.view'] },
      { name: 'Maintenance Requests', path: '/maintenance', permissions: ['maintenance.view', 'maintenance.manage'] },
    ],
  },
  {
    title: 'Transport',
    icon: Car,
    items: [
      { name: 'Trip Logs', path: '/admin/transport/logs', permissions: ['transport.view_all', 'transport.approve'], featureFlag: 'transport_logging_enabled' },
      { name: 'Vehicles', path: '/admin/transport/vehicles', permissions: ['transport.manage_vehicles', 'vehicles.view'], featureFlag: 'transport_logging_enabled' },
      { name: 'Settings', path: '/admin/transport/settings', permissions: ['transport.configure'], featureFlag: 'transport_logging_enabled' },
    ],
  },
  {
    title: 'Administration',
    icon: Settings,
    items: [
      { name: 'Users', path: '/admin/users', permissions: ['auth.manage_users'] },
      { name: 'NDIS Master Catalogue', path: '/admin/ndis-catalogue', permissions: ['roster.view'], requireRole: 'Super Admin' },
      { name: 'Roles', path: '/admin/roles', permissions: ['auth.manage_roles'] },
      { name: 'Care-Plan Templates', path: '/admin/care-plan-templates', permissions: ['careplan_templates.view'], featureFlag: 'participant_careplan_template_management_enabled' },
      { name: 'Security', path: '/admin/security', permissions: ['security.audit.view'] },
      { name: 'Security Alerts', path: '/admin/security/alerts', permissions: ['security.audit.view'] },
      { name: 'Public Forms', path: '/settings/public-forms', permissions: ['org.view'] },
      { name: 'Settings', path: '/org/settings', permissions: ['org.view'] },
      { name: 'Feature Flags', path: '/admin/settings/feature-flags', platformOnly: true, permissions: ['org.manage_settings'] },
      { name: 'Webhooks', path: '/settings/integrations/webhooks', permissions: ['integrations.webhooks.view'] },
      { name: 'Ratio Rules', path: '/admin/settings/ratio-rules', allowRoles: ['Admin'], permissions: ['org.manage_settings'] },
      { name: 'Sleep-Over Settings', path: '/admin/settings/sleepover', allowRoles: ['Admin'], permissions: ['org.manage_settings'], featureFlag: 'sleepover_billing_enabled' },
      { name: 'Sleep-Over Comparison', path: '/admin/sleepover/estimate-comparison', allowRoles: ['Admin'], permissions: ['roster.view'], featureFlag: 'sleepover_billing_enabled' },
      { name: 'Group Sessions', path: '/admin/group-sessions', permissions: ['roster.view', 'roster.manage'] },
      { name: 'Rollout Control', path: '/admin/rollout', platformOnly: true, permissions: ['org.manage_settings'] },
      { name: 'Release Sign-off', path: '/admin/release-signoff', platformOnly: true, permissions: ['org.manage_settings'] },
      { name: 'Daily Digest', path: '/admin/digest', permissions: ['org.manage_settings'] },
      { name: 'System Admin', path: '/admin/system', permissions: ['security.audit.view'] },
      { name: 'Audit Reporting', path: '/admin/audit-reporting', permissions: ['reports.manage', 'audit.read'] },
      { name: 'Soft Launch', path: '/admin/soft-launch', platformOnly: true, permissions: ['org.manage_settings'] },
      { name: 'Branding', path: '/admin/settings/branding', platformOnly: true, permissions: ['org.manage_settings'] },   // backend also requires a platform admin (require_platform_global)
      { name: 'Client Onboarding', path: '/admin/onboarding', platformOnly: true, permissions: ['org.manage_settings'], requireRole: 'Super Admin', featureFlag: 'client_onboarding_enabled' },
    ],
  },
];

// ════════════════════════════════════════════════════════════════
// STAFF SIDEBAR — Simplified, NO financial/admin data
// ════════════════════════════════════════════════════════════════
export const staffSidebarConfig = [
  {
    title: 'My Work',
    icon: LayoutDashboard,
    items: [
      { name: 'Dashboard', path: '/staff/dashboard', permissions: [] },
      { name: 'My Work Overview', path: '/my/work', permissions: [] },
      { name: 'My Roster', path: '/staff/roster', permissions: ['roster.view'] },
      { name: 'Clock In / Out', path: '/my/clock', permissions: ['attendance.clock_in'] },
      { name: 'My Timesheets', path: '/timesheets/my-v2', permissions: ['timesheets.submit'] },
      { name: 'Feedback About My Shifts', path: '/staff/feedback', permissions: ['matching.feedback.respond'] },
      { name: 'Open Shifts', path: '/staff/open-shifts', permissions: ['roster.open_shift_pickup'] },
      { name: 'Shift Swaps', path: '/my/swap-requests', permissions: ['roster.request_swap'] },
      { name: 'My Leave', path: '/portal/leave', permissions: ['leave.request'] },
      { name: 'Availability', path: '/portal/availability', permissions: ['availability.manage'] },
      { name: 'My Trip Logs', path: '/my/trip-logs', permissions: ['transport.log', 'transport.view_own'], featureFlag: 'transport_logging_enabled', badge: 'transport_pending' },
      { name: 'My Expenses', path: '/my/expenses', permissions: ['expenses.submit', 'expenses.view_own'] },
    ],
  },
  {
    title: 'Workplace',
    icon: Home,
    items: [
      { name: 'Houses', path: '/accommodation/houses', permissions: ['houses.view'] },
      { name: 'Incidents', path: '/compliance/incidents', permissions: ['incidents.view', 'incidents.create'] },
      { name: 'Messages', path: '/engagement/messages', permissions: ['engagement.messages.view'] },
    ],
  },
];

// Quick actions shortcut menu
export const QUICK_ACTIONS = [
  { name: 'New Referral', path: '/intake/referrals?new=1', icon: FileText },
  { name: 'New Participant', path: '/participants/list', icon: Users },
  { name: 'New Staff', path: '/staff/list', icon: UserCircle },
  { name: 'Create Roster', path: '/roster/scheduling-board', icon: Calendar },
  { name: 'Create Invoice', path: '/finance/invoices', icon: FileText },
  { name: 'Log Incident', path: '/compliance/incidents', icon: AlertTriangle },
];
